import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, type BadgeTone } from '@artosera/ui';
import { creerGalerieOuverture, transitionOuverture } from '../../../lib/api.js';
import { formaterOctets } from '../../../lib/format.js';
import { formeSlugValide } from '../../../lib/slug.js';
import { useLibellesModules } from '../../../lib/modules.js';
import {
  SOUS_ETAPES,
  etapeCochee,
  formaterJour,
  messageErreurOuverture,
} from '../../../lib/ouvertures.js';
import type { DossierOuverture, EtapeCreationOuverture } from '../../../types.js';
import styles from '../Ouvertures.module.css';
import type { PropsEtape } from './commun.js';

type EtatSousEtape = 'fait' | 'sans_objet' | 'en_cours' | 'refuse' | 'afaire';
const TONE: Record<EtatSousEtape, BadgeTone> = {
  fait: 'available',
  sans_objet: 'neutral',
  en_cours: 'reserved',
  refuse: 'sold',
  afaire: 'neutral',
};

/** Les états d'où l'API accepte la création (un brouillon, seulement pour un espace de test). */
function creationPermise(dossier: DossierOuverture): boolean {
  if (dossier.etat === 'brouillon') return dossier.identite.estTest;
  return ['contrat_signe', 'provisionne', 'reprise'].includes(dossier.etat);
}

/** Ce qui manque au dossier pour créer la galerie : la clé de son message. */
function manques(dossier: DossierOuverture): string[] {
  return [
    ...(formeSlugValide(dossier.identite.slug) ? [] : ['slug']),
    ...(dossier.identite.nomCommercial.trim() === '' ? ['nom'] : []),
    ...(dossier.equipe.direction === null ? ['direction'] : []),
    ...(!dossier.identite.estTest &&
    dossier.etat === 'brouillon' &&
    (dossier.contrat.reference.trim() === '' || dossier.contrat.signeLe === null)
      ? ['contrat']
      : []),
  ];
}

function Fait({ cle, children }: { cle: string; children: ReactNode }): JSX.Element {
  return (
    <div className={styles.faitsLigne}>
      <dt>{cle}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Étape 6 : le récapitulatif, puis la création idempotente de la galerie. */
export function EtapeCreation({
  dossier,
  lectureSeule,
  adopter,
  recharger,
  allerA,
}: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const [enCreation, setEnCreation] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<{
    faites: EtapeCreationOuverture[];
    envoyees: number;
  } | null>(null);
  const [provisionne, setProvisionne] = useState<boolean | null>(null);

  if (!dossier) return <p className={styles.info}>{t('ouvertures.assistant.verrouille')}</p>;

  const absents = manques(dossier);
  const creee = dossier.galleryId !== null;
  const commencee = dossier.etapes.length > 0;
  // Une sous-étape « à faire » ou refusée : la création se reprend là où elle s'est arrêtée.
  const incomplete = SOUS_ETAPES.some((cle) => {
    const etat = etapeCochee(dossier, cle)?.etat;
    return etat === undefined || etat === 'refuse';
  });
  const peutCreer = !lectureSeule && absents.length === 0 && creationPermise(dossier);

  const creer = async (): Promise<void> => {
    setErreur(null);
    setProvisionne(null);
    setEnCreation(true);
    try {
      const reponse = await creerGalerieOuverture(dossier.id);
      adopter(reponse);
      setResultat({ faites: reponse.faites, envoyees: reponse.invitationsEnvoyees });
      if (reponse.transitionsPossibles.includes('provisionne')) {
        try {
          adopter(
            await transitionOuverture(reponse.id, {
              vers: 'provisionne',
              revision: reponse.revision,
            }),
          );
          setProvisionne(true);
        } catch {
          setProvisionne(false);
        }
      }
    } catch (echec) {
      setErreur(messageErreurOuverture(t, echec));
      // Les sous-étapes déjà faites restent cochées côté serveur : on les relit.
      await recharger();
    } finally {
      setEnCreation(false);
    }
  };

  const identite = dossier.identite;
  const offre = dossier.offre;
  const equipe = dossier.equipe;
  const piece = dossier.contrat.piece;
  const sections: Array<{ cle: string; etape: number; contenu: JSX.Element }> = [
    {
      cle: 'galerie',
      etape: 1,
      contenu: (
        <dl className={styles.faits}>
          <Fait cle={t('ouvertures.galerie.nom')}>{identite.nomCommercial}</Fait>
          <Fait cle={t('ouvertures.galerie.adresseSite')}>
            {identite.slug
              ? `${identite.slug}.artosera.gallery`
              : t('ouvertures.creation.nonRenseigne')}
          </Fait>
          <Fait cle={t('ouvertures.galerie.raisonSociale')}>
            {identite.raisonSociale || t('ouvertures.creation.nonRenseigne')}
          </Fait>
          <Fait cle={t('ouvertures.galerie.reglages')}>
            {[
              t(`ouvertures.galerie.langues.${identite.langue}`),
              identite.devise,
              identite.fuseau,
            ].join(' · ')}
          </Fait>
          {identite.estTest ? (
            <Fait cle={t('ouvertures.galerie.estTest')}>{t('espaceTest.badge')}</Fait>
          ) : null}
        </dl>
      ),
    },
    {
      cle: 'offre',
      etape: 2,
      contenu: (
        <dl className={styles.faits}>
          <Fait cle={t('ouvertures.offre.palier')}>{t(`galeries.palier.${offre.palier}`)}</Fait>
          <Fait cle={t('ouvertures.offre.modules')}>
            {offre.modules.length > 0
              ? libelles.liste(offre.modules)
              : t('ouvertures.creation.aucun')}
          </Fait>
          <Fait cle={t('ouvertures.offre.addons')}>
            {offre.addons.length > 0
              ? offre.addons
                  .map(
                    (droit) =>
                      `${libelles.libelle(droit.addon)} (${t(`ouvertures.offre.modes.${droit.mode}`)}${
                        droit.jusquAu ? `, ${formaterJour(droit.jusquAu)}` : ''
                      })`,
                  )
                  .join(', ')
              : t('ouvertures.creation.aucun')}
          </Fait>
          <Fait cle={t('ouvertures.offre.services')}>
            {offre.services.length > 0
              ? libelles.liste(offre.services)
              : t('ouvertures.creation.aucun')}
          </Fait>
        </dl>
      ),
    },
    {
      cle: 'contrat',
      etape: 3,
      contenu: identite.estTest ? (
        <p className={styles.groupeTexte}>{t('ouvertures.creation.sansContrat')}</p>
      ) : (
        <dl className={styles.faits}>
          <Fait cle={t('ouvertures.contrat.reference')}>
            {dossier.contrat.reference || t('ouvertures.creation.nonRenseigne')}
          </Fait>
          <Fait cle={t('ouvertures.contrat.signeLe')}>
            {dossier.contrat.signeLe
              ? formaterJour(dossier.contrat.signeLe)
              : t('ouvertures.creation.nonRenseigne')}
          </Fait>
          <Fait cle={t('ouvertures.contrat.piece')}>
            {piece ? formaterOctets(piece.octets) : t('ouvertures.contrat.pieceAucune')}
          </Fait>
        </dl>
      ),
    },
    {
      cle: 'equipe',
      etape: identite.estTest ? 1 : 4,
      contenu: (
        <dl className={styles.faits}>
          <Fait cle={t('ouvertures.equipe.direction')}>
            {equipe.direction
              ? `${equipe.direction.nom} · ${equipe.direction.email}`
              : t('ouvertures.creation.nonRenseigne')}
          </Fait>
          <Fait cle={t('ouvertures.equipe.membres')}>
            {equipe.membres.length > 0
              ? equipe.membres
                  .map((m) => `${m.nom} (${t(`ouvertures.equipe.roles.${m.role}`)})`)
                  .join(', ')
              : t('ouvertures.creation.aucun')}
          </Fait>
          <Fait cle={t('ouvertures.equipe.invitations')}>
            {t(`ouvertures.equipe.invitationsOptions.${equipe.invitations}`)}
          </Fait>
        </dl>
      ),
    },
    {
      cle: 'prereglages',
      etape: 5,
      contenu: (
        <dl className={styles.faits}>
          <Fait cle={t('ouvertures.prereglages.mentions')}>
            {dossier.prereglages.mentionsComplementaires.length > 0
              ? dossier.prereglages.mentionsComplementaires.join(' / ')
              : t('ouvertures.creation.aucun')}
          </Fait>
          <Fait cle={t('ouvertures.prereglages.domaine')}>
            {dossier.prereglages.domaine ?? t('ouvertures.creation.aucun')}
          </Fait>
        </dl>
      ),
    },
  ];

  const statut = (cle: EtapeCreationOuverture): EtatSousEtape =>
    etapeCochee(dossier, cle)?.etat ?? 'afaire';

  return (
    <div className={styles.etape}>
      <header>
        <h2>{t('ouvertures.creation.titre')}</h2>
        <p>{t('ouvertures.creation.texte')}</p>
      </header>

      <div className={styles.recap}>
        {sections.map(({ cle, etape, contenu }) => (
          <section
            key={cle}
            className={styles.recapSection}
            aria-label={t(`ouvertures.creation.sections.${cle}`)}
          >
            <div className={styles.recapTete}>
              <h3>{t(`ouvertures.creation.sections.${cle}`)}</h3>
              {!lectureSeule && !creee ? (
                <Button variant="ghost" size="sm" onClick={() => allerA(etape)}>
                  {t('ouvertures.creation.modifier')}
                </Button>
              ) : null}
            </div>
            {contenu}
          </section>
        ))}
      </div>

      {absents.length > 0 ? (
        <div className={styles.info}>
          <strong>{t('ouvertures.creation.prerequis')}</strong>
          <ul className={styles.manques}>
            {absents.map((cle) => (
              <li key={cle}>{t(`ouvertures.creation.manque.${cle}`)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {commencee || creee ? (
        <section aria-label={t('ouvertures.creation.sousEtapes')}>
          <h3 className={styles.titreSousEtapes}>{t('ouvertures.creation.sousEtapes')}</h3>
          <ul className={styles.sousEtapes}>
            {SOUS_ETAPES.map((cle) => {
              const etat = statut(cle);
              const report = cle === 'invitations' && etat === 'en_cours';
              const note = etapeCochee(dossier, cle)?.note ?? '';
              return (
                <li
                  key={cle}
                  className={`${styles.sousEtape} ${
                    etat === 'fait' || etat === 'sans_objet' ? styles.sousEtapeFaite : ''
                  } ${etat === 'refuse' ? styles.sousEtapeRefusee : ''}`}
                  data-sous-etape={cle}
                  data-etat={etat}
                >
                  <span>
                    {t(`ouvertures.creation.sousEtape.${cle}`)}
                    {report && equipe.invitations === 'a_la_bascule' ? (
                      <span className={styles.sousEtapeNote}>
                        {t('ouvertures.creation.notes.invitations')}
                      </span>
                    ) : note ? (
                      <span className={styles.sousEtapeNote}>{note}</span>
                    ) : null}
                  </span>
                  <Badge tone={TONE[etat]}>{t(`ouvertures.creation.etat.${etat}`)}</Badge>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {resultat ? (
        <div className={styles.resultat} role="status">
          <p>
            {resultat.faites.length > 0
              ? t('ouvertures.creation.faitesParAppel', {
                  liste: resultat.faites
                    .map((cle) => t(`ouvertures.creation.sousEtape.${cle}`))
                    .join(', '),
                })
              : t('ouvertures.creation.rienAFaire')}{' '}
            {t('ouvertures.creation.invitationsEnvoyees', { count: resultat.envoyees })}
          </p>
          <p>
            <strong>{t('ouvertures.creation.termine')}</strong>{' '}
            {provisionne === true
              ? t('ouvertures.creation.provisionne')
              : provisionne === false
                ? t('ouvertures.creation.provisionneEchec')
                : null}
          </p>
        </div>
      ) : null}

      {erreur ? (
        <div role="alert" className={`${styles.info} ${styles.erreurCreation}`}>
          <p>{erreur}</p>
          <p>{t('ouvertures.creation.echec')}</p>
        </div>
      ) : null}

      <div className={styles.actionsEtape}>
        <Button variant="ghost" onClick={() => allerA(dossier.identite.estTest ? 1 : 5)}>
          {t('ouvertures.assistant.precedent')}
        </Button>
        <div>
          {creee ? (
            <>
              <Link to={`/galeries/${dossier.galleryId}`} className="lien-cible">
                {t('ouvertures.creation.ouvrirGalerie')}
              </Link>
              <Link to={`/ouvertures/${dossier.id}`} className="lien-cible">
                {t('ouvertures.creation.ouvrirDossier')}
              </Link>
            </>
          ) : null}
          {!creee || incomplete ? (
            <Button onClick={() => void creer()} loading={enCreation} disabled={!peutCreer}>
              {commencee
                ? enCreation
                  ? t('ouvertures.creation.reprise')
                  : t('ouvertures.creation.reprendre')
                : enCreation
                  ? t('ouvertures.creation.creation')
                  : t('ouvertures.creation.cree')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
